import * as React from 'react';

import {Api} from 'nutshell-core/api';

const PIPELINE_TASKS_CONTAINER_ID = '#lead-processes-container';
const CUSTOM_FIELDS_CONTAINER_ID = '#top-custom-fields-container';
const SIDEBAR_CUSTOM_FIELDS_CONTAINER_ID = '#sidebar-custom-fields-container';

// Our API class defaults its baseUrl to be `/rest/`, so we
// need to construct a new one and override that here as we're
// not hitting the rest API for these billing calls.
const viewApi = new Api('/');
const headers = new Headers({
    'Content-Type': 'application/xhtml+xml',
    accept: '*/*',
    'X-Requested-With': 'XMLHttpRequest',
});

type UseGetLeadMarkupReturn = {
    pipelineTasks: HTMLElement | null | undefined;
    pipelineTasksCount: number;
    customFields: HTMLElement | null | undefined;
    customFieldsCount: number;
    sidebarCustomFields: HTMLElement | null | undefined;
    sidebarCustomFieldsCount: number;
    isLoading: boolean;
    setDidRenderPipelineTasks: (arg1: boolean) => void;
    setDidRenderCustomFields: (arg1: boolean) => void;
    setDidRenderSidebarCustomFields: (arg1: boolean) => void;
};

type StageTaskCounts = {
    [key: string]: number;
};

/**
 * Parses the server side content and sets the state for the various sections of
 * the lead page. This is temporary until we transition the the few remaining
 * sections to react.
 */
export function useGetLeadMarkup(leadId?: string, leadNum?: number): UseGetLeadMarkupReturn {
    const [pipelineTasks, setPipelineTasks] = React.useState<HTMLElement | null | undefined>(null);
    const [pipelineTasksCount, setPipelineTasksCount] = React.useState<number>(0);
    const [stageTaskCounts, setStageTaskCounts] = React.useState<StageTaskCounts>({});
    const [customFields, setCustomFields] = React.useState<HTMLElement | null | undefined>(null);
    const [customFieldsCount, setCustomFieldsCount] = React.useState<number>(0);
    const [sidebarCustomFields, setSidebarCustomFields] = React.useState<
        HTMLElement | null | undefined
    >(null);
    const [sidebarCustomFieldsCount, setSidebarCustomFieldsCount] = React.useState<number>(0);
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const [didRenderPipelineTasks, setDidRenderPipelineTasks] = React.useState<boolean>(false);
    const [didRenderCustomFields, setDidRenderCustomFields] = React.useState<boolean>(false);
    const [didRenderSidebarCustomFields, setDidRenderSidebarCustomFields] =
        React.useState<boolean>(false);

    function getSelectedStageId(): string | null | undefined {
        const selected = document.querySelector(
            '.stages-carousel-wrapper .ui-lead-stage--selected'
        );
        if (!selected) return null;
        const attrVal = selected.getAttribute('data-stage-id') || (selected as any).dataset.stageId;
        if (!attrVal) return null;

        return String(attrVal).replace('-stages', '');
    }

    const getLeadMarkup = React.useCallback((id?: string, num?: number) => {
        let url;
        if (id) {
            url = `/lead/render-server-content.webchunk?id=${id}`;
        } else if (num) {
            url = `/lead/render-server-content.webchunk?number=${num}`;
        }

        if (url) {
            setIsLoading(true);
            setDidRenderPipelineTasks(false);
            setDidRenderCustomFields(false);
            setPipelineTasks(null);
            setPipelineTasksCount(0);
            setStageTaskCounts({});

            viewApi
                // @ts-expect-error
                .get(url, undefined, headers)
                .then((res) => {
                    // @ts-expect-error
                    if (res && res.redirected) {
                        // @ts-expect-error
                        window.location.href = res.url;
                    }

                    return res;
                })
                // @ts-expect-error
                .then((res) => res.text())
                .then((body) => {
                    const parser = new DOMParser();

                    const doc = parser.parseFromString(body, 'text/html');

                    const processesContainer = doc.querySelector(PIPELINE_TASKS_CONTAINER_ID);

                    const customFieldsContainer = doc.querySelector(CUSTOM_FIELDS_CONTAINER_ID);
                    const cardContainer =
                        customFieldsContainer &&
                        customFieldsContainer.querySelector('#custom-field-cards-container');

                    const topCustomFieldsCount =
                        // @ts-expect-error
                        cardContainer && cardContainer.dataset.customFieldsCount
                            ? // @ts-expect-error
                              Number(cardContainer.dataset.customFieldsCount)
                            : 0;

                    const sidebarCustomFieldsContainer = doc.querySelector(
                        SIDEBAR_CUSTOM_FIELDS_CONTAINER_ID
                    );
                    const sidebarFieldsCount =
                        sidebarCustomFieldsContainer &&
                        // @ts-expect-error
                        sidebarCustomFieldsContainer.dataset.customFieldsCount
                            ? // @ts-expect-error
                              Number(sidebarCustomFieldsContainer.dataset.customFieldsCount)
                            : 0;

                    // Build a map of milestone id -> task count
                    const counts: StageTaskCounts = {};
                    if (processesContainer) {
                        const milestoneEls = processesContainer.querySelectorAll(
                            '.process-milestone-container'
                        );
                        for (let i = 0; i < milestoneEls.length; i++) {
                            const el = milestoneEls[i] as any;
                            const milestoneId =
                                el.getAttribute('data-milestone-id') ||
                                (el.dataset ? el.dataset.milestoneId : null);
                            const countVal =
                                el.getAttribute('data-stage-task-count') ||
                                (el.dataset ? el.dataset.stageTaskCount : null);
                            if (milestoneId) {
                                counts[String(milestoneId)] = countVal ? Number(countVal) : 0;
                            }
                        }
                    }

                    setStageTaskCounts(counts);

                    // Determine current selected stage and set initial count
                    const selectedStageId = getSelectedStageId();
                    const initialCount =
                        selectedStageId && counts[selectedStageId] ? counts[selectedStageId] : 0;

                    // @ts-expect-error
                    setPipelineTasks(processesContainer);
                    setPipelineTasksCount(initialCount);
                    // @ts-expect-error
                    setCustomFields(customFieldsContainer);
                    // @ts-expect-error
                    setSidebarCustomFields(sidebarCustomFieldsContainer);
                    setSidebarCustomFieldsCount(sidebarFieldsCount);
                    setCustomFieldsCount(topCustomFieldsCount);
                })
                .catch(() => {
                    // TODO: handle errors properly
                })
                .finally(() => {
                    setIsLoading(false);
                });
        }
    }, []);

    React.useEffect(() => {
        getLeadMarkup(leadId, leadNum);
    }, [getLeadMarkup, leadId, leadNum]);

    // Keep the pipelineTasks in sync with the selected stage
    React.useEffect(() => {
        function updateCountFromSelection() {
            const selectedStageId = getSelectedStageId();
            if (selectedStageId && stageTaskCounts && stageTaskCounts[selectedStageId] != null) {
                setPipelineTasksCount(stageTaskCounts[selectedStageId]);
            } else if (selectedStageId) {
                setPipelineTasksCount(0);
            }

            // Update the state-active class on milestone containers which the
            // old sales process workspace tab needs to update it's display
            if (selectedStageId) {
                const milestoneContainers = document.querySelectorAll(
                    '.process-milestone-container'
                );
                for (let i = 0; i < milestoneContainers.length; i++) {
                    const container = milestoneContainers[i];
                    // @ts-expect-error
                    const milestoneId = container.getAttribute('data-milestone-id');

                    if (milestoneId === selectedStageId) {
                        // @ts-expect-error
                        container.classList.add('state-active');
                    } else {
                        // @ts-expect-error
                        container.classList.remove('state-active');
                    }
                }
            }
        }

        updateCountFromSelection();

        // @ts-expect-error
        const stageEvent = window.Nut.modules.Process.events.ActiveStageChanged;
        if (typeof $ !== 'undefined' && stageEvent) {
            $(window).on(stageEvent, updateCountFromSelection);

            return () => {
                $(window).off(stageEvent, updateCountFromSelection);
            };
        }
    }, [stageTaskCounts]);

    // We need to initialize jQuery after all of our markup has been rendered in
    // their individual widgets
    React.useEffect(() => {
        if (
            (pipelineTasks &&
                customFields &&
                sidebarCustomFields &&
                didRenderPipelineTasks &&
                didRenderCustomFields &&
                didRenderSidebarCustomFields) ||
            NutClientConfig.hasLeadPageRedesignM3FF
        ) {
            if (leadId) {
                Nut.preparePage();
                Nut.modules.Lead.initializeOldBackboneModelForLeadId(leadId);
                Nut.updateAppearances();
            }
        }
    }, [
        leadId,
        didRenderPipelineTasks,
        didRenderCustomFields,
        didRenderSidebarCustomFields,
        pipelineTasks,
        customFields,
        sidebarCustomFields,
    ]);

    return {
        pipelineTasks,
        pipelineTasksCount,
        customFields,
        customFieldsCount,
        sidebarCustomFields,
        sidebarCustomFieldsCount,
        isLoading,
        setDidRenderPipelineTasks,
        setDidRenderCustomFields,
        setDidRenderSidebarCustomFields,
    };
}
